/** @jsx onetext.jsx.element */

import type { OneText } from '../../../global';

// eslint-disable-next-line no-restricted-syntax
import { BrandBackgroundNewYear as BrandBackground, BrandLogoWhite as BrandLogo } from '../components';
// eslint-disable-next-line no-restricted-syntax
import { defaultBrandConfig, SUPPRESSED_ROUTES } from '../config';
// eslint-disable-next-line no-restricted-syntax
import { NewYearBanner, NewYearCake as HeroGraphic } from '../images';

import type { GetPopupAPIResponse, PopupConfig } from '@onetext/api';
import { POPUP_LAYOUT } from '@onetext/api';

declare let onetext : OneText;

const { clsx, deepmerge } = onetext.jsx;

const {
    Popup,
    Page,
    EmailField,
    PhoneField,
    Button,
    Row,
    Columns,
    Column,
    Quiz,
    QuizOption,
    Terms,
    Teaser,
    PopupContainer
} = onetext.component.consent.popup;

const customBrandConfig : PopupConfig = {
    style: {
        color: {
            text:       '#d38478',
            background: '#FDF6E2'
        }
    },
    component: {
        button: {
            level: {
                primary: {
                    style: {
                        text: {
                            base:  '#ffffff',
                            hover: '#ffffff'
                        },
                        background: {
                            base:  'rgba(186,196,161,1.0)',
                            hover: 'rgba(186,196,161,0.65)'
                        }
                    }
                }
            }
        }
    }
};

type NewYearPopupProps = {
    popup : GetPopupAPIResponse,
};

export const NewYearPopup = ({
    popup
} : NewYearPopupProps) : JSX.Element | null => {
    const brandConfig = deepmerge(defaultBrandConfig, customBrandConfig);
    return (
        <PopupContainer
            popup={ { ...popup, config: brandConfig } }
            suppressedRoutes={ SUPPRESSED_ROUTES }>
            <Popup
                layout={ POPUP_LAYOUT.FULLSCREEN }
                background={ <BrandBackground /> }>
                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <BrandLogo />
                                <p className={
                                    clsx(
                                        'text-xl',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center -mt-5'
                                    )
                                }>
                                    <img
                                        src={ NewYearBanner }
                                        width={ 180 } />
                                </p>
                                <p className={
                                    clsx(
                                        'text-6xl md:text-8xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-semibold',
                                        'my-1'
                                    )
                                }>
                                    { '20% off' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-3xl',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'mb-4'
                                    )
                                }>
                                    { 'Sitewide!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-4 mb-6',
                                        'w-[280px] font-medium'
                                    )
                                }>
                                    { 'To claim your discount, tell us the age you\'re shopping for today:' }
                                </p>
                                <Quiz propertyName={ 'shopping_age' }>
                                    <div className={
                                        clsx(
                                            'w-[264px]',
                                            'flex flex-col',
                                            'items-center',
                                            'gap-2.5'
                                        )
                                    }>
                                        <QuizOption propertyValue={ 'newborn' }>
                                            <Button>
                                                { 'Newborn' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '6m_24m' }>
                                            <Button>
                                                { '6M - 24M' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '2t_4t' }>
                                            <Button>
                                                { '2T - 4T' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '5_years_plus' }>
                                            <Button>
                                                { '5 YEARS+' }
                                            </Button>
                                        </QuizOption>
                                    </div>
                                </Quiz>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <img
                                    src={ HeroGraphic }
                                    width={ 300 } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>

                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <BrandLogo />
                                <p className={
                                    clsx(
                                        'text-xl',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center -mt-5'
                                    )
                                }>
                                    <img
                                        src={ NewYearBanner }
                                        width={ 180 } />
                                </p>
                                <p className={
                                    clsx(
                                        'text-6xl md:text-8xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-semibold',
                                        'my-1'
                                    )
                                }>
                                    { '20% off' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-3xl',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'mb-4'
                                    )
                                }>
                                    { 'Sitewide!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-4 mb-6',
                                        'w-[280px] font-medium'
                                    )
                                }>
                                    { 'Sign up with your email address to claim your 20% discount' }
                                </p>
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex flex-col',
                                        'items-center',
                                        'gap-2.5'
                                    )
                                }>
                                    <EmailField autofocus={ true } />
                                    <Button>
                                        { 'Continue' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <img
                                    src={ HeroGraphic }
                                    width={ 300 } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>

                <Page>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-[40px]',
                                        'font-semibold',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { 'Finish signing up' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-3 mb-6',
                                        'w-[280px]',
                                        'md:whitespace-nowrap font-medium'
                                    )
                                }>
                                    { 'with text to activate your 20% off discount' }
                                </p>
                                <Terms
                                    brandName={ 'Caden Lane' }
                                    termsUrl={ 'https://cadenlane.com/Pages/terms-of-service' }
                                    privacyUrl={ 'https://cadenlane.com/Pages/privacy-policy' }
                                />
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex flex-col',
                                        'items-center',
                                        'gap-2.5',
                                        'mt-8'
                                    )
                                }>
                                    <PhoneField autofocus={ true } />
                                    <Button>
                                        { 'Activate 20% Off' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <img
                                    src={ HeroGraphic }
                                    width={ 300 } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>

                <Page>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-2xl',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'mb-4'
                                    )
                                }>
                                    { 'You\'ve got' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-6xl md:text-8xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-semibold',
                                        'mb-1'
                                    )
                                }>
                                    { '20% off!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-2xl',
                                        'font-light',
                                        'tracking-wider',
                                        'text-center',
                                        'w-[280px]',
                                        'mt-7 mb-5'
                                    )
                                }>
                                    { 'Use code at checkout:' }
                                </p>
                                <div className={
                                    clsx(
                                        'flex flex-col items-center justify-center',
                                        'bg-[rgba(255,255,255,0.34)]',
                                        'border-4 border-dashed border-black',
                                        'text-[#1C1D1D] fira-sans text-base',
                                        'h-16 px-5 py-[18px]',
                                        'relative z-[1]',
                                        'mb-10'
                                    )
                                }>
                                    <div className={
                                        clsx(
                                            'text-black fira-sans',
                                            'text-xl font-extrabold',
                                            'tracking-[1px] leading-5',
                                            'text-center'
                                        )
                                    }>
                                        { 'NEWYEAR20' }
                                    </div>
                                </div>
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex flex-col',
                                        'items-center',
                                        'gap-2.5'
                                    )
                                }>
                                    <Button>
                                        { 'Shop now' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <img
                                    src={ HeroGraphic }
                                    width={ 300 } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>
            </Popup>
            <Teaser customText={ 'Get 20% off' } />
        </PopupContainer>
    );
};
